/*
 * Required table (run in Supabase SQL editor if not yet created):
 *
 *   CREATE TABLE IF NOT EXISTS cookie_consent_logs (
 *     id         uuid        PRIMARY KEY DEFAULT gen_random_uuid(),
 *     decision   text        NOT NULL,   -- 'accepted' | 'declined'
 *     locale     text,
 *     created_at timestamptz NOT NULL DEFAULT now()
 *   );
 *   ALTER TABLE cookie_consent_logs ENABLE ROW LEVEL SECURITY;
 *   -- Allow anonymous inserts (no auth required)
 *   CREATE POLICY "anon insert" ON cookie_consent_logs FOR INSERT TO anon WITH CHECK (true);
 *
 *   -- early_access_signups also requires an anon INSERT policy:
 *   CREATE POLICY "anon insert" ON early_access_signups FOR INSERT TO anon WITH CHECK (true);
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePortalLanguage } from "@/contexts/PortalLanguageContext";
import { supabase } from "@/lib/supabase/client";

const STORAGE_KEY = "fyrris_cookie_consent";
const EMBER = "#FF6B35";

export default function CookieBanner() {
  const { t } = usePortalLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const dismiss = (choice: "accepted" | "declined") => {
    localStorage.setItem(STORAGE_KEY, choice);
    setVisible(false);
    ;(async () => {
      try {
        await supabase.from("cookie_consent_logs").insert({ decision: choice, locale: navigator.language });
      } catch (err) {
        console.error(err);
      }
    })();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9998] border-t border-gray-800"
      style={{ backgroundColor: "#1A1A2E" }}
      role="region"
      aria-label="Cookie consent"
    >
      <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        {/* Text */}
        <p className="flex-1 text-sm text-gray-300 leading-relaxed">
          {t("cookie.text", "We use cookies to improve your experience and analyze site traffic.")}{" "}
          <Link
            to="/privacy"
            className="underline underline-offset-2 transition-opacity hover:opacity-75 whitespace-nowrap"
            style={{ color: EMBER }}
          >
            {t("cookie.learnMore", "Learn more")}
          </Link>
        </p>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:flex-shrink-0">
          <button
            onClick={() => dismiss("declined")}
            className="w-full sm:w-auto inline-flex items-center justify-center rounded-full border border-gray-600 px-6 py-2 text-sm font-medium text-gray-300 whitespace-nowrap transition hover:border-gray-400 hover:text-white"
          >
            {t("cookie.decline", "Decline")}
          </button>
          <button
            onClick={() => dismiss("accepted")}
            className="w-full sm:w-auto inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-semibold text-white whitespace-nowrap transition hover:opacity-90"
            style={{ backgroundColor: EMBER }}
          >
            {t("cookie.accept", "Accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
